import { CommonModule } from '@angular/common';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Observable, finalize } from 'rxjs';
import { SystemSettingsService } from '../../../core/services/system-settings.service';
import { showAlertDialog } from '../../../shared/helpers/showAlertDialog';
import { AlertType } from '../../../shared/models/alertType';

interface EditTemplateDialogData {
  type: string;
  name: string;
  template: string;
  saveFn?: (payload: { type: string; template: string }) => Observable<void>;
  successTitle?: string;
  successMessage?: string;
  errorTitle?: string;
  errorMessage?: string;
}

@Component({
  selector: 'app-edit-notification-template-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule],
  templateUrl: './edit-notification-template-dialog.component.html',
})
export class EditNotificationTemplateDialogComponent {
  @ViewChild('templateInput') templateInput?: ElementRef<HTMLTextAreaElement>;

  readonly variables = [
    { token: '{{consultantName}}', label: 'Nome do Consultor' },
    { token: '{{storeName}}', label: 'Nome da Loja' },
    { token: '{{baseAddress}}', label: 'Endereço da Base' },
    { token: '{{routeName}}', label: 'Nome da Rota' },
    { token: '{{itineraryName}}', label: 'Nome do Roteiro' },
    { token: '{{routeStartTime}}', label: 'Horário de Início da Rota' },
    { token: '{{routeEndTime}}', label: 'Horário de Finalização da Rota' },
    { token: '{{lunchStartTime}}', label: 'Início do Horário de Almoço Programado' },
    { token: '{{lunchEndTime}}', label: 'Final do Horário de Almoço Programado' },
  ] as const;

  readonly form = this.formBuilder.group({
    template: [this.data.template, [Validators.required]],
  });

  isSubmitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: EditTemplateDialogData,
    private readonly dialogRef: MatDialogRef<EditNotificationTemplateDialogComponent>,
    private readonly formBuilder: FormBuilder,
    private readonly settingsService: SystemSettingsService,
    private readonly dialog: MatDialog,
  ) {}

  close(): void {
    this.dialogRef.close();
  }

  addVariable(token: string): void {
    const control = this.form.controls.template;
    const input = this.templateInput?.nativeElement;

    if (!input) {
      control.setValue(`${control.value ?? ''}${token}`);
      control.markAsDirty();
      return;
    }

    const text = control.value ?? '';
    const start = input.selectionStart ?? text.length;
    const end = input.selectionEnd ?? text.length;
    const nextText = `${text.slice(0, start)}${token}${text.slice(end)}`;

    control.setValue(nextText);
    control.markAsDirty();

    setTimeout(() => {
      input.focus();
      const cursor = start + token.length;
      input.setSelectionRange(cursor, cursor);
    });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const template = this.form.controls.template.value?.trim() ?? '';
    this.isSubmitting = true;

    const payload = { type: this.data.type, template };
    const save$ = this.data.saveFn
      ? this.data.saveFn(payload)
      : this.settingsService.updateNotificationTemplate(payload);

    save$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          showAlertDialog(
            this.dialog,
            AlertType.Success,
            this.data.successTitle ?? 'Sucesso!',
            this.data.successMessage ?? 'Texto da notificação atualizado com sucesso.',
          )
            .afterClosed()
            .subscribe(() => {
              this.dialogRef.close({
                updated: true,
                template,
              });
            });
        },
        error: () => {
          showAlertDialog(
            this.dialog,
            AlertType.Error,
            this.data.errorTitle ?? 'Erro',
            this.data.errorMessage ?? 'Não foi possível atualizar o texto da notificação.',
          );
        },
      });
  }
}
